import React, { createContext, useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send credentials (cookies) with every request
axios.defaults.withCredentials = true;

// Track if we're currently refreshing to prevent multiple simultaneous refresh calls
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Setup axios interceptor for automatic token refresh on 401
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry refresh or logout endpoints
      if (originalRequest.url?.includes('/auth/refresh') || 
          originalRequest.url?.includes('/auth/logout') ||
          originalRequest.url?.includes('/auth/login')) {
        return Promise.reject(error);
      }
      
      if (isRefreshing) {
        // Wait for the refresh to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return axios(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        // Try to refresh the token
        await axios.post(`${API}/auth/refresh`);
        processQueue(null);
        isRefreshing = false;
        // Retry the original request
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        isRefreshing = false;
        // Refresh failed - user needs to login again
        // The AuthProvider will handle clearing state
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

const AuthContext = createContext();

/**
 * useAuth Hook - Access authentication context
 * Must be used within AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * AuthProvider - Manages authentication state and actions
 * Uses httpOnly cookies for secure token storage
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [justLoggedIn, setJustLoggedIn] = useState(false); // Flag for showing grind intent

  // Memoized fetchUserInfo to use in useEffect
  const fetchUserInfo = useCallback(async () => {
    try {
      // Cookie will be sent automatically with withCredentials: true
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Token invalid or expired - user is not authenticated
      // No need to clear storage - cookies are httpOnly and server-managed
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize auth state on mount - check if user has valid session cookie
  useEffect(() => {
    // With httpOnly cookies, we just try to fetch user info
    // If the cookie is valid, we'll get user data; otherwise 401
    fetchUserInfo();
    
    // Legacy cleanup - remove old localStorage tokens during migration
    try {
      localStorage.removeItem('token');
      sessionStorage.removeItem('auth_token');
    } catch (e) {
      // Storage not available - ignore
    }
  }, [fetchUserInfo]);
  
  // Separate effect for service worker and PWA setup
  useEffect(() => {
    registerServiceWorker();
    setupPWAInstallPrompt();
  }, []);
  
  const setupPWAInstallPrompt = useCallback(() => {
    if (typeof window === 'undefined') return;
    
    let deferredPrompt = null;
    
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      deferredPrompt = e;
    };
    
    const handleAppInstalled = () => {
      deferredPrompt = null;
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const registerServiceWorker = useCallback(async () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return;
    
    if ('serviceWorker' in navigator && 'Notification' in window) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/'
        });

        if (Notification.permission === 'default') {
          await Notification.requestPermission();
        }

        if ('sync' in ServiceWorkerRegistration.prototype) {
          const readyReg = await navigator.serviceWorker.ready;
          await readyReg.sync.register('affirmation-check');
        }
      } catch (error) {
        // Service worker registration failed - non-critical
      }
    }
  }, []);

  const login = useCallback(async (username, password) => {
    try {
      // Cookies are automatically handled with withCredentials: true
      const response = await axios.post(`${API}/auth/login`, { username, password });
      
      if (response.data.requires_verification) {
        toast.info('Please verify your email to continue');
        return {
          success: false,
          requiresVerification: true,
          email: response.data.email,
          message: response.data.message
        };
      }
      
      const { user: userData } = response.data;
      // Tokens are now stored in httpOnly cookies by the server
      // No need to store in localStorage/sessionStorage
      setUser(userData);
      
      // Set flag to show grind intent on dashboard - EVERY login
      setJustLoggedIn(true);
      
      toast.success('Welcome back, athlete!');
      return { success: true };
    } catch (error) {
      let errorMessage = 'Invalid credentials';
      
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail
            .map(err => err.msg || err.message || 'Validation error')
            .join(', ');
        }
      }
      
      toast.error(errorMessage);
      return { success: false };
    }
  }, []);

  const register = useCallback(async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      
      if (response.data.message && response.data.message.includes('Verification code')) {
        return {
          success: true,
          needsVerification: true,
          email: response.data.email,
          pendingRegistrationId: response.data.pending_registration_id
        };
      }
      
      const { user: newUser } = response.data;
      // Tokens are now stored in httpOnly cookies by the server
      setUser(newUser);
      
      toast.success('Welcome to your athlete journey!');
      return { success: true, needsVerification: false };
    } catch (error) {
      let errorMessage = 'Registration failed';
      
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail
            .map(err => err.msg || err.message || 'Validation error')
            .join(', ');
        }
      }
      
      toast.error(errorMessage);
      return { success: false };
    }
  }, []);
  
  const verifyEmail = useCallback(async (email, code) => {
    try {
      const response = await axios.post(`${API}/auth/verify-email`, { email, code });
      const { user: newUser } = response.data;
      // Tokens are now stored in httpOnly cookies by the server
      setUser(newUser);
      
      toast.success('Email verified! Welcome to Grind.365!');
      return true;
    } catch (error) {
      let errorMessage = 'Verification failed';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      toast.error(errorMessage);
      return false;
    }
  }, []);
  
  const resendVerificationCode = useCallback(async (email) => {
    try {
      await axios.post(`${API}/auth/resend-verification`, null, { params: { email } });
      toast.success('Verification code resent to your email');
      return true;
    } catch (error) {
      let errorMessage = 'Failed to resend code';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      toast.error(errorMessage);
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // Call server to clear httpOnly cookies
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      // Even if server call fails, clear local state
    }
    setUser(null);
    toast.success('Logged out successfully');
  }, []);

  const refreshUser = useCallback(async () => {
    await fetchUserInfo();
  }, [fetchUserInfo]);

  // Clear the justLoggedIn flag after showing grind intent
  const clearJustLoggedIn = useCallback(() => {
    setJustLoggedIn(false);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    login,
    register,
    logout,
    refreshUser,
    loading,
    verifyEmail,
    resendVerificationCode,
    justLoggedIn,
    clearJustLoggedIn
  }), [user, login, register, logout, refreshUser, loading, verifyEmail, resendVerificationCode, justLoggedIn, clearJustLoggedIn]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
